import { useEffect, useState } from "react";
import TableComponent from "../../components/table/TableComponent";
import { AdminInterface } from "../../interfaces/admin.interface";
import CreateOrUpdateAdminModal from "./CreateOrUpdateAdminModal";
import { MdAdd, MdEdit } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { DispatchType } from "../../interfaces/redux.interface";
import { getAdmins, updateAdmin } from "../../redux/slices/AdminSlice";
import { FormControlLabel, Radio, RadioGroup, Switch } from "@mui/material";
import { successToast } from "../../utils/toast.util";
import { Column } from "../../interfaces/table.interface";
import {
  TicketPermissionInterface,
  TicketPermissionValue,
} from "../../interfaces/ticket-permission.interface";
import { get, put } from "../../redux/ApiHelper";

const AdminList = () => {
  const dispatch: DispatchType = useDispatch();
  const { adminList }: { adminList: AdminInterface[] } = useSelector(
    (state: any) => state.admin
  );
  const [filteredAdminList, setFilteredAdminList] = useState<AdminInterface[]>(
    []
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editData, setEditData] = useState<AdminInterface>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [ticketPermissionMap, setTicketPermissionMap] = useState<
    Record<string, TicketPermissionValue>
  >({});

  useEffect(() => {
    dispatch(getAdmins());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setFilteredAdminList(adminList);
  }, [adminList]);

  useEffect(() => {
    const fetchTicketPermissions = async () => {
      try {
        const response = await get("/admin/ticket/permission/all");
        const permissions: TicketPermissionInterface[] =
          response?.ticketPermissions || [];
        const nextPermissionMap: Record<string, TicketPermissionValue> = {};

        permissions.forEach((permission) => {
          if (permission?.adminId) {
            nextPermissionMap[permission.adminId] = permission.permission;
          }
        });

        setTicketPermissionMap(nextPermissionMap);
      } catch (error) {
        console.error(error);
      }
    };

    fetchTicketPermissions();
  }, [adminList]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const lowerQuery = query.toLowerCase();

    const filteredList = adminList.filter((admin) => {
      return (
        admin.name?.toLowerCase().includes(lowerQuery) ||
        admin.phoneNumber?.toString().toLowerCase().includes(lowerQuery) ||
        admin.email?.toLowerCase().includes(lowerQuery)
      );
    });

    setFilteredAdminList(filteredList);
  };

  // Function to open modal
  const openModal = () => {
    setIsModalOpen(true);
  };

  // Function to close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const updateTicketPermission = async (
    rowData: AdminInterface,
    permission: TicketPermissionValue
  ) => {
    const adminId = rowData?._id;
    const adminRole = rowData?.role || "admin";
    const previousPermission = adminId
      ? ticketPermissionMap[adminId] || "none"
      : "none";

    if (!adminId) return;

    setTicketPermissionMap((prev) => ({
      ...prev,
      [adminId]: permission,
    }));

    try {
      await put("/admin/ticket/permission", {
        adminRole,
        adminId,
        permission,
      });
      successToast("Chat access updated successfully");
    } catch (error) {
      setTicketPermissionMap((prev) => ({
        ...prev,
        [adminId]: previousPermission,
      }));
      console.error(error);
    }
  };

  const columns: Column[] = [
    { select: "name", header: "Name", align: "left" },
    { select: "phoneNumber", header: "Phone Number" },
    { select: "email", header: "Email" },
    { select: "pinCode", header: "Pin code" },
    { select: "type", header: "Admin Type" },
    {
      select: "isActive",
      header: "Status",
      render: (value: boolean, rowData: AdminInterface) => (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={value}
              onChange={(e) =>
                dispatch(
                  updateAdmin({
                    id: rowData?._id,
                    data: { isActive: e.target.checked },
                  })
                )
                  .unwrap()
                  .then(() => {
                    successToast("Status updated successfully");
                  })
              }
            />
          }
          label="Active"
          labelPlacement="end" // 'start', 'top', 'bottom' are also valid options
        />
      ),
    },
    {
      header: "Chat Access (Parent, Teacher)",
      render: (value: unknown, rowData: AdminInterface) => {
        const selectedPermission =
          ticketPermissionMap[rowData?._id || ""] || "none";

        return (
          <div className="text-left">
            <div className="text-xs font-semibold mb-1">Chat Access</div>
            <RadioGroup
              row
              value={selectedPermission}
              onChange={(e) =>
                updateTicketPermission(
                  rowData,
                  e.target.value as TicketPermissionValue
                )
              }
            >
              <FormControlLabel value="none" control={<Radio size="small" />} label="None" />
              <FormControlLabel value="parent" control={<Radio size="small" />} label="Parent" />
              <FormControlLabel value="teacher" control={<Radio size="small" />} label="Teacher" />
              <FormControlLabel value="both" control={<Radio size="small" />} label="Both" />
            </RadioGroup>
          </div>
        );
      },
    },
    {
      header: "Edit",
      render: (value: boolean, rowData: AdminInterface) => (
        <div
          className="rounded-full bg-slate-200 p-2 text-yellow-500 cursor-pointer"
          onClick={() => {
            setEditData(rowData);
            setIsEdit(true);
            setIsModalOpen(true);
          }}
        >
          <MdEdit className="m-auto" />
        </div>
      ),
    },
  ];

  return (
    <div className="admin table-container">
      <div className="title">Admin List</div>
      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search admin"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <TableComponent columns={columns} data={filteredAdminList} />

      {/* Floating Action Button */}
      <div
        onClick={openModal}
        className="fixed top-8 right-8 bg-yellow-500 text-white p-2 rounded-full cursor-pointer shadow-lg hover:bg-yellow-600"
      >
        <MdAdd />
      </div>

      {/* Create Admin Modal */}
      <CreateOrUpdateAdminModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        isEdit={isEdit}
        editData={editData}
      />
    </div>
  );
};

export default AdminList;
